import React, { useState, useEffect, useCallback } from 'react';
import { MdRefresh, MdBarChart, MdEventAvailable, MdAccessTime, MdStar, MdMeetingRoom, MdPeople, MdTrendingUp, MdFilterList, MdClear } from 'react-icons/md';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { conferenceRoomApi } from '../../../api/adminapi';
import './WorkspaceAnalytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const STATUS_COLORS = {
  PENDING: '#FF9800',
  APPROVED: '#4CAF50',
  REJECTED: '#F44336',
  CANCELLED: '#607D8B',
  AUTO_CANCELLED: '#9E9E9E',
  COMPLETED: '#2196F3',
};

const STATUS_LABELS = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  AUTO_CANCELLED: 'Auto Cancelled',
};

const CHART_PALETTE = [
  '#704493', '#2196F3', '#FF9800', '#4CAF50', '#F44336',
  '#9C27B0', '#00BCD4', '#607D8B', '#E91E63', '#3F51B5',
  '#009688', '#FF5722', '#795548', '#CDDC39', '#03A9F4',
];

function WorkspaceAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await conferenceRoomApi.getBookingAnalytics(dateFrom || null, dateTo || null, selectedLocation || null);
      setAnalytics(data);
    } catch (err) {
      console.error('Analytics load error:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedLocation]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedLocation('');
  };

  const hasFilters = dateFrom || dateTo || selectedLocation;

  if (loading) {
    return (
      <div className="workspace-analytics">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workspace-analytics">
        <div className="error-message">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadAnalytics}>
            <MdRefresh /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const {
    totalBookings, statusBreakdown = {}, monthlyTrend = {}, roomWise = [],
    roomTypeBreakdown = {}, locationBreakdown = {}, dailyTrend = [],
    peakHours = {}, topBookers = [], avgDurationMinutes = 0,
    avgFeedbackRating = 0, feedbackStats = {}, approvalRate = 0,
    cancellationCount = 0, pendingCount = 0, availableLocations = []
  } = analytics;

  const approved = statusBreakdown['APPROVED'] || 0;
  const rejected = statusBreakdown['REJECTED'] || 0;

  // ===== CHART DATA =====

  // 1. Monthly Trend - Stacked Bar
  const monthKeys = Object.keys(monthlyTrend);
  const allStatuses = [...new Set(monthKeys.flatMap(m => Object.keys(monthlyTrend[m])))];
  const monthlyChartData = {
    labels: monthKeys,
    datasets: allStatuses.map(status => ({
      label: STATUS_LABELS[status] || status,
      data: monthKeys.map(m => monthlyTrend[m]?.[status] || 0),
      backgroundColor: (STATUS_COLORS[status] || '#999') + 'CC',
      borderColor: STATUS_COLORS[status] || '#999',
      borderWidth: 1,
      borderRadius: 3,
    })),
  };

  // 2. Status Donut
  const statusLabels = Object.keys(statusBreakdown);
  const statusChartData = {
    labels: statusLabels.map(s => STATUS_LABELS[s] || s),
    datasets: [{
      data: statusLabels.map(s => statusBreakdown[s]),
      backgroundColor: statusLabels.map(s => STATUS_COLORS[s] || '#999'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  // 3. Room Type Donut
  const roomTypeLabels = Object.keys(roomTypeBreakdown);
  const roomTypeChartData = {
    labels: roomTypeLabels,
    datasets: [{
      data: roomTypeLabels.map(rt => roomTypeBreakdown[rt]),
      backgroundColor: CHART_PALETTE.slice(0, roomTypeLabels.length),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  // 4. Location Donut
  const locationLabels = Object.keys(locationBreakdown);
  const locationChartData = {
    labels: locationLabels,
    datasets: [{
      data: locationLabels.map(l => locationBreakdown[l]),
      backgroundColor: CHART_PALETTE.slice(0, locationLabels.length),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  // 5. Top Rooms Bar
  const topRoomsChartData = {
    labels: roomWise.map(r => r.roomName),
    datasets: [{
      label: 'Bookings',
      data: roomWise.map(r => r.count),
      backgroundColor: CHART_PALETTE.map(c => c + 'CC'),
      borderColor: CHART_PALETTE,
      borderWidth: 1,
      borderRadius: 4,
    }],
  };

  // 6. Daily Trend Line
  const dailyChartData = {
    labels: dailyTrend.map(d => {
      const dt = new Date(d.date);
      return dt.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' });
    }),
    datasets: [{
      label: 'Daily Bookings',
      data: dailyTrend.map(d => d.count),
      borderColor: '#704493',
      backgroundColor: 'rgba(112, 68, 147, 0.1)',
      fill: true,
      tension: 0.4,
      pointRadius: 2,
      pointHoverRadius: 5,
      borderWidth: 2,
    }],
  };

  // 7. Peak Hours Bar
  const hourLabels = Object.keys(peakHours);
  const peakHoursChartData = {
    labels: hourLabels,
    datasets: [{
      label: 'Bookings',
      data: hourLabels.map(h => peakHours[h]),
      backgroundColor: hourLabels.map((_, i) => {
        const val = peakHours[hourLabels[i]];
        const max = Math.max(...Object.values(peakHours));
        const intensity = max > 0 ? val / max : 0;
        return `rgba(112, 68, 147, ${0.3 + intensity * 0.7})`;
      }),
      borderColor: '#704493',
      borderWidth: 1,
      borderRadius: 3,
    }],
  };

  // 8. Top Bookers Bar
  const topBookersChartData = {
    labels: topBookers.map(u => u.userName),
    datasets: [{
      label: 'Bookings',
      data: topBookers.map(u => u.count),
      backgroundColor: CHART_PALETTE.map(c => c + 'BB'),
      borderColor: CHART_PALETTE,
      borderWidth: 1,
      borderRadius: 4,
    }],
  };

  const chartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } },
      title: { display: !!title, text: title, font: { size: 13, weight: '600' }, color: '#1e293b', padding: { bottom: 10 } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } },
    },
  });

  const stackedOptions = (title) => ({
    ...chartOptions(title),
    scales: {
      ...chartOptions(title).scales,
      x: { ...chartOptions(title).scales.x, stacked: true },
      y: { ...chartOptions(title).scales.y, stacked: true },
    },
  });

  const donutOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 10, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } },
      title: { display: !!title, text: title, font: { size: 13, weight: '600' }, color: '#1e293b', padding: { bottom: 8 } },
    },
    cutout: '60%',
  });

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 9 }, maxTicksLimit: 15 } },
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 10 } } },
    },
  };

  return (
    <div className="workspace-analytics">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <MdBarChart className="header-icon" />
          <h2>Workspace Booking Analytics</h2>
          {hasFilters && <span className="filter-active-badge">Filtered</span>}
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={() => setShowFilters(!showFilters)}>
            <MdFilterList /> {showFilters ? 'Hide Filters' : 'Filters'}
          </button>
          {hasFilters && (
            <button className="btn btn-outline" onClick={clearFilters}>
              <MdClear /> Clear
            </button>
          )}
          <button className="btn btn-primary" onClick={loadAnalytics}>
            <MdRefresh /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>From Date</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Location</label>
            <select value={selectedLocation} onChange={e => setSelectedLocation(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.82rem', minWidth: '150px' }}>
              <option value="">All Locations</option>
              {availableLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={loadAnalytics}>Apply</button>
        </div>
      )}

      {/* Stat Cards */}
      <div className="stat-cards">
        <div className="stat-card stat-total">
          <div className="stat-icon"><MdEventAvailable /></div>
          <div className="stat-info">
            <span className="stat-value">{totalBookings}</span>
            <span className="stat-label">Total Bookings</span>
          </div>
        </div>
        <div className="stat-card stat-approved">
          <div className="stat-icon"><MdTrendingUp /></div>
          <div className="stat-info">
            <span className="stat-value">{approved}</span>
            <span className="stat-label">Approved</span>
          </div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-icon"><MdAccessTime /></div>
          <div className="stat-info">
            <span className="stat-value">{pendingCount}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="stat-card stat-rejected">
          <div className="stat-icon"><MdClear /></div>
          <div className="stat-info">
            <span className="stat-value">{rejected}</span>
            <span className="stat-label">Rejected</span>
          </div>
        </div>
        <div className="stat-card stat-cancelled">
          <div className="stat-icon"><MdClear /></div>
          <div className="stat-info">
            <span className="stat-value">{cancellationCount}</span>
            <span className="stat-label">Cancelled</span>
          </div>
        </div>
        <div className="stat-card stat-duration">
          <div className="stat-icon"><MdAccessTime /></div>
          <div className="stat-info">
            <span className="stat-value">{avgDurationMinutes}m</span>
            <span className="stat-label">Avg Duration</span>
          </div>
        </div>
        <div className="stat-card stat-approval">
          <div className="stat-icon"><MdTrendingUp /></div>
          <div className="stat-info">
            <span className="stat-value">{approvalRate}%</span>
            <span className="stat-label">Approval Rate</span>
          </div>
        </div>
        <div className="stat-card stat-rating">
          <div className="stat-icon"><MdStar /></div>
          <div className="stat-info">
            <span className="stat-value">{avgFeedbackRating}</span>
            <span className="stat-label">Avg Rating</span>
          </div>
        </div>
      </div>

      {/* Monthly Trend - Full Width */}
      <div className="chart-section full-width">
        <div className="chart-card">
          <h3>Month-on-Month Booking Trend</h3>
          <div className="chart-container chart-tall">
            {monthKeys.length > 0 ? (
              <Bar data={monthlyChartData} options={stackedOptions(null)} />
            ) : (
              <div className="no-data">No monthly data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Trend Line - Full Width */}
      <div className="chart-section full-width">
        <div className="chart-card">
          <h3>Daily Booking Trend</h3>
          <div className="chart-container chart-tall">
            {dailyTrend.length > 0 ? (
              <Line data={dailyChartData} options={lineOptions} />
            ) : (
              <div className="no-data">No daily data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Row: Status + Room Type + Location Donuts */}
      <div className="chart-section three-col">
        <div className="chart-card">
          <h3>Status Distribution</h3>
          <div className="chart-container chart-donut">
            {statusLabels.length > 0 ? (
              <Doughnut data={statusChartData} options={donutOptions(null)} />
            ) : (
              <div className="no-data">No data</div>
            )}
          </div>
        </div>
        <div className="chart-card">
          <h3>By Room Type</h3>
          <div className="chart-container chart-donut">
            {roomTypeLabels.length > 0 ? (
              <Doughnut data={roomTypeChartData} options={donutOptions(null)} />
            ) : (
              <div className="no-data">No data</div>
            )}
          </div>
        </div>
        <div className="chart-card">
          <h3>By Location</h3>
          <div className="chart-container chart-donut">
            {locationLabels.length > 0 ? (
              <Doughnut data={locationChartData} options={donutOptions(null)} />
            ) : (
              <div className="no-data">No data</div>
            )}
          </div>
        </div>
      </div>

      {/* Row: Top Rooms + Peak Hours */}
      <div className="chart-section two-col">
        <div className="chart-card">
          <h3><MdMeetingRoom style={{ verticalAlign: 'middle', marginRight: 6 }} />Most Booked Rooms</h3>
          <div className="chart-container chart-tall">
            {roomWise.length > 0 ? (
              <Bar data={topRoomsChartData} options={chartOptions(null)} />
            ) : (
              <div className="no-data">No room data available</div>
            )}
          </div>
        </div>
        <div className="chart-card">
          <h3><MdAccessTime style={{ verticalAlign: 'middle', marginRight: 6 }} />Peak Booking Hours</h3>
          <div className="chart-container chart-tall">
            {hourLabels.length > 0 ? (
              <Bar data={peakHoursChartData} options={{ ...chartOptions(null), plugins: { ...chartOptions(null).plugins, legend: { display: false } } }} />
            ) : (
              <div className="no-data">No peak hour data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Top Bookers + Feedback Stats */}
      <div className="chart-section two-col">
        <div className="chart-card">
          <h3><MdPeople style={{ verticalAlign: 'middle', marginRight: 6 }} />Top Bookers</h3>
          <div className="chart-container chart-tall">
            {topBookers.length > 0 ? (
              <Bar data={topBookersChartData} options={{ ...chartOptions(null), indexAxis: 'y', plugins: { ...chartOptions(null).plugins, legend: { display: false } } }} />
            ) : (
              <div className="no-data">No user data available</div>
            )}
          </div>
        </div>
        <div className="chart-card">
          <h3><MdStar style={{ verticalAlign: 'middle', marginRight: 6 }} />Feedback & Compliance</h3>
          <div className="feedback-stats-grid">
            <div className="feedback-stat">
              <div className="feedback-value">{avgFeedbackRating || '-'}<span className="feedback-unit">/5</span></div>
              <div className="feedback-label">Avg Rating</div>
            </div>
            <div className="feedback-stat">
              <div className="feedback-value">{feedbackStats.totalFeedback || 0}</div>
              <div className="feedback-label">Total Reviews</div>
            </div>
            <div className="feedback-stat">
              <div className="feedback-value">{feedbackStats.roomCleanPct || 0}%</div>
              <div className="feedback-label">Room Clean</div>
            </div>
            <div className="feedback-stat">
              <div className="feedback-value">{feedbackStats.equipmentOkPct || 0}%</div>
              <div className="feedback-label">Equipment OK</div>
            </div>
            <div className="feedback-stat">
              <div className="feedback-value">{feedbackStats.endedOnTimePct || 0}%</div>
              <div className="feedback-label">Ended On Time</div>
            </div>
            <div className="feedback-stat">
              <div className="feedback-value">{approvalRate}%</div>
              <div className="feedback-label">Approval Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Rooms Table */}
      {roomWise.length > 0 && (
        <div className="chart-section full-width">
          <div className="chart-card">
            <h3>Room Usage Details</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Room Name</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Tower</th>
                    <th>Floor</th>
                    <th>Bookings</th>
                  </tr>
                </thead>
                <tbody>
                  {roomWise.map((room, idx) => (
                    <tr key={room.roomId}>
                      <td>{idx + 1}</td>
                      <td><strong>{room.roomName}</strong></td>
                      <td><span className="room-type-badge">{room.roomType}</span></td>
                      <td>{room.location}</td>
                      <td>{room.tower || '-'}</td>
                      <td>{room.floor || '-'}</td>
                      <td><strong>{room.count}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkspaceAnalytics;
